import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatterDocumentType } from '@models/matter-file';
import { DocumentAITextLoadingUIService } from '@services/document-ai/document-ai-loading-ui.service';
import {
	DocumentProcessingLoadingKey,
	DocumentProcessorService,
} from '@services/document-ai/document-processor.service';
import { interval, Observable } from 'rxjs';
import { map, startWith, filter, switchMap, take } from 'rxjs/operators';

@Component({
	selector: 'app-ai-document-processing-progress-bar',
	templateUrl: './ai-document-processing-progress-bar.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AiDocumentProcessingProgressBarComponent {
	public isOCRProcessing$: Observable<boolean>;
	public ocrProcessingPercentage$: Observable<number>;
	public currentProgressMessage$: Observable<string>;
	@Input() messagePosition: 'center' | 'start' = 'center';
	@Input() set matterDocumentType(
		matterDocumentType: DocumentProcessingLoadingKey,
	) {
		this.isOCRProcessing$ =
			this.documentProcessorService.isLoading$(matterDocumentType);
		const [progressBarMessages, estimatedLoadingTime] =
			this.documentAITextLoadingUIService.getLoadingMetaData(
				matterDocumentType.replace(/:\.*/, '') as MatterDocumentType,
			);

		this.ocrProcessingPercentage$ = this.documentProcessorService
			.getLoadingTimestamp(matterDocumentType)
			.pipe(
				filter((x) => !!x),
				map((x) => {
					const currentProgressPercentage =
						((new Date().getTime() - x) / estimatedLoadingTime) * 99;
					return Math.floor(currentProgressPercentage);
				}),
				switchMap((x) =>
					interval(estimatedLoadingTime / 100).pipe(
						map((i) => x + i),
						take(100 - x),
					),
				),
			);

		this.currentProgressMessage$ = this.documentProcessorService
			.getLoadingTimestamp(matterDocumentType)
			.pipe(
				filter((x) => !!x),
				switchMap((x) => {
					const elapsedTime = new Date().getTime() - x;
					const percentageElapsed = elapsedTime / estimatedLoadingTime;
					const messageIndex = Math.floor(
						percentageElapsed * progressBarMessages.length,
					);

					return interval(
						estimatedLoadingTime / progressBarMessages.length,
					).pipe(
						startWith(messageIndex),
						map((i) => progressBarMessages[i]),
						take(progressBarMessages.length - messageIndex + 1),
					);
				}),
			);
	}

	constructor(
		private documentProcessorService: DocumentProcessorService,
		private documentAITextLoadingUIService: DocumentAITextLoadingUIService,
	) {}
}
